"use client";

import { useEffect, useState } from "react";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/next";
import { redactAnalyticsUrl } from "@/lib/analytics/redact-url";

const CONSENT_KEY = "growthsync-analytics-consent";

export function AnalyticsWithConsent() {
  const [hasConsent, setHasConsent] = useState<boolean | null>(null);

  useEffect(() => {
    // Check if user has previously given consent
    if (typeof window !== "undefined") {
      const consent = localStorage.getItem(CONSENT_KEY);
      if (consent === "true") {
        setHasConsent(true);
        // Dev-only: Log when analytics is enabled (helps verify it's working)
        if (process.env.NODE_ENV === "development") {
          console.log("[Analytics] ✅ Enabled - Vercel Analytics will track usage");
        }
      } else if (consent === "false") {
        setHasConsent(false);
        // Dev-only: Log when analytics is disabled
        if (process.env.NODE_ENV === "development") {
          console.log("[Analytics] ❌ Disabled - No tracking will occur");
        }
      } else {
        // No consent decision yet - default to false for GDPR compliance
        // User can enable in settings
        setHasConsent(false);
        if (process.env.NODE_ENV === "development") {
          console.log("[Analytics] ❌ Disabled (default) - No tracking until user enables in Settings");
        }
      }
    }
  }, []);

  // Only render Analytics if user has consented
  // CRITICAL: This is the only place Analytics component is rendered
  // If hasConsent is not true, Analytics will NOT initialize and NO tracking will occur
  if (hasConsent === true) {
    return (
      <>
        <Analytics
          beforeSend={(event) => {
            const url = redactAnalyticsUrl(event.url);
            return url ? { ...event, url } : null;
          }}
        />
        {/*
          SpeedInsights fires its own page-view event with the same resolved browser URL as
          Analytics above, so without its own beforeSend it still ships whatever
          redactAnalyticsUrl exists to strip (referral codes, invite tokens, record ids),
          just through this sibling component instead of the one that was fixed.
        */}
        <SpeedInsights
          beforeSend={(data) => {
            const url = redactAnalyticsUrl(data.url);
            return url ? { ...data, url } : null;
          }}
        />
      </>
    );
  }

  // Return null when disabled - this ensures Analytics component is never rendered
  // and no tracking scripts are loaded
  return null;
}

export function getAnalyticsConsent(): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(CONSENT_KEY) === "true";
}

export function setAnalyticsConsent(consent: boolean): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(CONSENT_KEY, String(consent));
}
